import axios from "axios";
import {
  TIIVII_API_OPERATIONS,
  TIIVII_API_DOWNLOAD_STREAMSOURCE_LOGS,
  TIIVII_API_DOWNLOAD_STREAMSOURCE_SPECIFIC_LOG,
  TIIVII_GET_STREAMSOURCES_ENDPOINT,
  TIIVII_GET_DEPLOYMENT_STREAMSOURCES_ENDPOINT,
} from "../utils/constants";
import { keycloak } from "../config/keycloak";
import { buildQueryParams } from "./commonService";

const transformSourceFromAPI = (source) => {
  const parseTimestamp = (timestamp) => {
    if (!timestamp) return null;
    const ts = typeof timestamp === 'string' ? parseInt(timestamp, 10) : timestamp;
    return ts ? new Date(ts * 1000).toISOString() : null;
  };

  return {
    id: source._id,
    description: source.description,
    use: source.use,
    type: source.type,
    brand: source.brand,
    owner: source.owner,
    model: source.model,
    serial: source.serial,
    lastStatus: source.status?.last_status,
    lastChecked: parseTimestamp(source.status?.last_checked),
    lastThumbnail: source.status?.last_thumbnail,
    publicInterface: source.public_interface ? {
      ip: source.public_interface.ip,
      adminTcpPort: source.public_interface.admin_tcp_port,
      videoStreamPort: source.public_interface.video_stream_port,
      adminUrl: source.public_interface.admin_url,
      streamTcpUrl: source.public_interface.stream_tcp_url,
      adminTls: source.public_interface.admin_tls,
      dns: source.public_interface.dns,
      dnsEntry: source.public_interface.dns_entry,
      protocol: source.public_interface.protocol,
      adminSslPort: source.public_interface.admin_ssl_port,
      secondCoachUrl: source.public_interface.second_coach_url,
      ptzControllerPort: source.public_interface.ptz_controller_port,
    } : null,
    deployment: source.deployment_id ? {
      id: source.deployment_id,
      codename: source.deployment_codename,
    } : null,
    privateInterface: source.private_interface,
    users: source.users,
    currentFirmware: source.current_firmware,
    firmwareUpdateAvailable: source.firmware_update_available,
    deploymentNeeds: source.deployment_needs,
    privacyActivated: source.privacy_activated,
    usuallyDisconnected: source.usually_disconnected,
    hasPrivacy: source.has_privacy,
    dewarpDisabled: source.dewarp_disabled,
  };
};

export const getSourcesService = async (params) => {
  const {
    offset,
    limit,
    deploymentId,
    deploymentIds,
    partnerId,
    hasAdminPrivileges,
    contractIds,
    status,
    withoutDeployment,
    usuallyDisconnected,
  } = params;

  if (!deploymentId) {
    const queryParams = buildQueryParams({
      status,
      withoutDeployment,
      usuallyDisconnected,
      partnerId,
      contractIds: contractIds?.join(','),
      offset,
      limit,
    });

    const url = `${TIIVII_GET_STREAMSOURCES_ENDPOINT}${queryParams ? `?${queryParams}` : ''}`;
    const options = getOptions({});

    const response = await axios.get(url, options);

    const total = parseInt(
      response.headers['pagination-total-results'] ||
        response.headers['Pagination-Total-Results'] ||
        response.headers['x-total-count'] ||
        response.headers['X-Total-Count'] ||
        '0',
      10,
    );

    return {
      items: response.data.map(transformSourceFromAPI),
      total,
    };
  } else {
    const isAuthorized = hasAdminPrivileges || (deploymentIds?.includes(deploymentId) ?? false);
    
    if (!isAuthorized) {
      throw new Error('Unauthorized to access deployment sources');
    }

    const queryParams = buildQueryParams({
      offset,
      limit,
    });

    const url = buildUrl(TIIVII_GET_DEPLOYMENT_STREAMSOURCES_ENDPOINT, { deployment_id: deploymentId });
    const fullUrl = `${url}${queryParams ? `?${queryParams}` : ''}`;
    const options = getOptions({});

    const response = await axios.get(fullUrl, options);
    
    const total = parseInt(
      response.headers['pagination-total-results'] ||
        response.headers['Pagination-Total-Results'] ||
        response.headers['x-total-count'] ||
        response.headers['X-Total-Count'] ||
        '0',
      10,
    );
    
    return {
      items: response.data.map(transformSourceFromAPI),
      total,
    };
  }
};

export const getStreamsourceAdvancedInfo = (params, thenCallback) => {
  const options = getOptions({});
  axios
    .post(
      buildUrl(TIIVII_API_OPERATIONS, {
        streamsource_id: params.streamsourceId,
      }),
      { action: params.action },
      options,
    )
    .then(response => {
      thenCallback(response.data);
    });
};

export const downloadStreamsourceLogs = (params, thenCallback) => {
    const options = getOptions({});
    axios
      .get(
        buildUrl(TIIVII_API_DOWNLOAD_STREAMSOURCE_LOGS, {
          streamsource_id: params.streamsourceId,
        }),
        options,
      )
      .then(response => {
        thenCallback(response.data);
      });
  };

  export const downloadStreamsourceSpecificLog = (params, thenCallback) => {
    const options = getOptions({});
    axios
      .get(
        buildUrl(TIIVII_API_DOWNLOAD_STREAMSOURCE_SPECIFIC_LOG, {
          streamsource_id: params.streamsourceId,
          logfile_id: params.logfile_id,
        }),
        options,
      )
      .then(response => {
        thenCallback(response.data);
      });
  };

/**
 * Dado un valor para url = 'https://bert.tiivii.com/api/studio/:id/event/:event_id' y un valor para variables = {
 *   id: "un_id", event_id: "un_event_id"}, devolvería https://bert.tiivii.com/api/studio/un_id/event/un_event_id
 * @param url Url con placeholders
 * @param variables objeto con las substituciones a realizar
 * @returns {string} con la url generada a partir de las substituciones.
 */
export const buildUrl = (url, variables) =>
  url.replace(/:(\w+)/g, (match, p1) => (p1 in variables ? variables[p1] : match));

const getOptions = (config = {}, withAuthentication = true) => {
  const configObject = {
    headers: {
      "Content-Type": "application/json",
      ...config.headers,
    },
    ...config,
  };
  if (withAuthentication) {
    configObject.headers.Authorization = `Bearer ${!!keycloak && !!keycloak.token ? keycloak.token : undefined}`;
  }

  return configObject;
};
const getOptionsUrlEncoded = (config = {}, withAuthentication = true) => {
  const configObject = {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      ...config.headers,
    },
    ...config,
  };
  if (withAuthentication) {
    configObject.headers.Authorization = `Bearer ${!!keycloak && !!keycloak.token ? keycloak.token : undefined}`;
  }

  return configObject;
};
